<script setup lang="ts">
import { useGameStore } from '~/stores/games.store';
import { HandThumbUpIcon, HeartIcon } from '@heroicons/vue/24/solid';
import LazyImage from '~/components/design-system/lazy-image.vue';
import { useFormatNumberCompact } from '~/composables/useFormatters';
import type { IGameCategory } from '~/interfaces/dto/games/response';
import type { TStandardImageKeys } from 'assets/data/branding-library';
import GameUrlWrapper from '~/components/category/game-url-wrapper.vue';
import type { IGameUrlWrapperProps } from '~/interfaces/components/game-url-wrapper';

interface CardProps {
  id?: string;
  image?: string;
  title?: string;
  game?: IGameCategory | null;
  position?: number;
  likesCount?: number;
  gameUrlWrapperProps?: Partial<IGameUrlWrapperProps>;
}

enum positions {
  'positionGold',
  'positionSilver',
  'positionBronze',
}

const props = withDefaults(defineProps<CardProps>(), {
  id: '0',
  image: '',
  title: '',
  game: null,
  position: -1,
  likesCount: 0,
  gameUrlWrapperProps: () => ({}),
});

const games = useGameStore();
const site = useSiteStore();
const colorMode = useColorMode();
const { getPlaceholder, getImage } = useBranding();

const webviewDarkMode = ref(false);
let observer: MutationObserver;

const gameUnavailable = computed(() => {
  if (props.game?.unavailable) return true;
  return games.returnDisabledProviders.includes(props.game?.provider);
});

const positionIcon = computed(() =>
  props?.position < 4
    ? getImage(positions[props.position] as TStandardImageKeys)
    : '',
);

const backgroundImage = computed(() => {
  const isDarkMode = site.isWebview
    ? webviewDarkMode.value
    : colorMode.preference === 'dark';
  const gradient = isDarkMode
    ? 'linear-gradient(270deg, rgba(32, 33, 38, 0.8) 0%, #131316 100%)'
    : 'linear-gradient(270deg, rgba(255, 255, 255, 0.90) 0%, #F0F0F2 100%)';
  if (!site.isWebview) return gradient;
});

const isFavorite = computed<boolean>(() => {
  return games.returnFavoriteIds.some((f) => f === props.id);
});

onMounted(() => {
  if (site.isWebview) {
    observer = new MutationObserver((mutationRecords) => {
      webviewDarkMode.value = (
        mutationRecords[0].target as Element
      ).classList?.contains('dark');
      observer.disconnect();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
});

onUnmounted(() => {
  if (site.isWebview) observer.disconnect();
});
</script>

<template>
  <GameUrlWrapper
    v-if="!!game"
    ref="cardHolster"
    class="flex flex-row text-base cursor-pointer rounded-xl overflow-hidden glow bg-center h-[60px] dark:bg-dark-700 bg-light-100"
    :class="[{ unavailable: gameUnavailable }]"
    :game="game"
    component="category-card-list"
    v-bind="gameUrlWrapperProps"
  >
    <div class="w-full flex bg-cover bg-center" :style="{ backgroundImage }">
      <div class="justify-center relative z-5 overflow-hidden min-w-[60px]">
        <LazyImage
          :id="id"
          class="h-[60px] w-[60px] overflow-hidden relative object-cover aspect-square p-1 rounded-lg"
          :src="image"
          :placeholder="getPlaceholder('categoryCardPlaceholder')"
          :alt="title"
        />
      </div>

      <div class="flex relative z-5 flex-row w-full">
        <div
          class="w-full overflow-hidden p-[10px] sm:p-2 text-base-priority text-xs text-left flex flex-col justify-around dark:text-light-50 text-dark-900"
        >
          <strong class="w-full line-clamp-1">
            {{ title || ' ' }}
          </strong>
          <div class="grid grid-cols-2 w-28 truncate capitalize">
            <div
              v-if="props?.game?.likesCount || props?.likesCount"
              class="flex items-center gap-0.5 mr-2 w-fit"
            >
              <HandThumbUpIcon class="!min-w-4 !max-w-4 mr-1" />
              {{
                useFormatNumberCompact(
                  props?.game?.likesCount || props?.likesCount,
                )
              }}
            </div>
            <div class="flex w-fit">
              <HeartIcon
                :class="[
                  '!min-w-4 !max-w-4',
                  {
                    'primary-pink-gradient-text': isFavorite,
                    'text-base': !isFavorite,
                  },
                ]"
              />
            </div>
          </div>
        </div>

        <div
          v-if="position > -1"
          class="w-fit dark:text-light-50 text-dark-900 flex flex-row justify-end content-center items-center mr-1"
        >
          <div
            class="bg-layer-secondary dark:text-light-50 text-dark-900 p-1 rounded-lg mr-2"
          >
            <LazyImage
              v-if="!!positionIcon"
              class="text-center w-8 h-6"
              loading="lazy"
              :src="positionIcon"
              :alt="String(position)"
            />
            <div v-else>{{ position + 1 }}th</div>
          </div>
        </div>
      </div>
    </div>
  </GameUrlWrapper>
</template>
